"use client";

import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { Bike, Info, MapPin, Train } from "lucide-react";
import { useState } from "react";

interface NeighborhoodMobilityProps {
  walkScore?: number;
  transitScore?: number;
  bikeScore?: number;
}

interface ScoreDetailsProps {
  value: number;
  type: "walk" | "transit" | "bike";
}

function getScoreDescription(
  score: number,
  type: "walk" | "transit" | "bike",
): string {
  if (type === "walk") {
    if (score >= 90) return "Walker's Paradise";
    if (score >= 70) return "Very Walkable";
    if (score >= 50) return "Somewhat Walkable";
    return "Car-Dependent";
  } else if (type === "transit") {
    if (score >= 90) return "Rider's Paradise";
    if (score >= 70) return "Excellent Transit";
    if (score >= 50) return "Good Transit";
    return "Limited Transit";
  } else {
    if (score >= 90) return "Biker's Paradise";
    if (score >= 70) return "Very Bikeable";
    if (score >= 50) return "Bikeable";
    return "Somewhat Bikeable";
  }
}

function getScoreColor(score: number): string {
  if (score >= 90) return "text-emerald-600 bg-emerald-50";
  if (score >= 70) return "text-blue-600 bg-blue-50";
  if (score >= 50) return "text-amber-600 bg-amber-50";
  return "text-gray-600 bg-gray-50";
}

function getGradientColor(score: number): string {
  if (score >= 90) return "from-emerald-500";
  if (score >= 70) return "from-blue-500";
  if (score >= 50) return "from-amber-500";
  return "from-gray-500";
}

function ScoreCard({ value, type }: ScoreDetailsProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const label = type.charAt(0).toUpperCase() + type.slice(1);
  const description = getScoreDescription(value, type);
  const scoreColor = getScoreColor(value);
  const gradientColor = getGradientColor(value);

  const icon =
    type === "walk" ? (
      <MapPin className="h-5 w-5" />
    ) : type === "transit" ? (
      <Train className="h-5 w-5" />
    ) : (
      <Bike className="h-5 w-5" />
    );

  const explanationText =
    type === "walk"
      ? "Score measures pedestrian-friendliness based on distances to amenities."
      : type === "transit"
        ? "Score measures access to public transit based on frequency and route options."
        : "Score measures the bikeability of an area based on infrastructure and terrain.";

  return (
    <div
      className={cn(
        "rounded-lg border border-gray-200 transition-all cursor-pointer hover:border-gray-300",
        isExpanded && "shadow-sm bg-gray-50/50",
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => setIsExpanded(!isExpanded)}
      role="button"
      tabIndex={0}
      aria-expanded={isExpanded}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          setIsExpanded(!isExpanded);
        }
      }}
    >
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className={cn("p-1.5 rounded-full", scoreColor)}>{icon}</div>
            <h4 className="font-semibold">{label} Score®</h4>
          </div>
          <div
            className={cn(
              "rounded-full h-9 w-14 flex items-center justify-center font-semibold",
              scoreColor,
            )}
          >
            {value}
          </div>
        </div>

        <div className="mt-1 relative h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={cn(
              "absolute top-0 left-0 h-full bg-gradient-to-r to-transparent",
              gradientColor,
            )}
            style={{ width: `${value}%` }}
          />
        </div>

        <p className="mt-2 text-sm text-muted-foreground">{description}</p>

        {/* Show explanation on hover for desktop, on click for mobile, or always for print */}
        {(isHovered || isExpanded) && (
          <div className="mt-2 text-xs text-muted-foreground">
            {explanationText}
          </div>
        )}

        {/* Always show for screen readers but visually hidden */}
        <p className="sr-only">{explanationText}</p>

        {!isExpanded && (
          <div className="mt-2 text-xs text-center text-gray-400 sm:hidden">
            Tap for more info
          </div>
        )}
      </div>
    </div>
  );
}

export function NeighborhoodMobility({
  walkScore,
  transitScore,
  bikeScore,
}: NeighborhoodMobilityProps) {
  // Don't render if all scores are missing or 0
  const hasAnyScore =
    (walkScore && walkScore > 0) ||
    (transitScore && transitScore > 0) ||
    (bikeScore && bikeScore > 0);

  if (!hasAnyScore) return null;

  // Calculate how many scores we have to adjust the grid layout
  const scoreCount = [walkScore, transitScore, bikeScore].filter(
    (score) => score && score > 0,
  ).length;
  const gridCols =
    scoreCount === 1
      ? "grid-cols-1"
      : scoreCount === 2
        ? "grid-cols-1 md:grid-cols-2"
        : "grid-cols-1 md:grid-cols-3";

  return (
    <Card>
      <CardContent className="p-6 space-y-4">
        <div>
          <h3 className="text-lg font-semibold">Neighborhood Mobility</h3>
          <p className="text-sm text-muted-foreground">
            How easily you can get around with or without a car
          </p>
        </div>

        <div className={cn("grid gap-4", gridCols)}>
          {walkScore && walkScore > 0 ? (
            <ScoreCard value={Math.round(walkScore)} type="walk" />
          ) : null}

          {transitScore && transitScore > 0 ? (
            <ScoreCard value={Math.round(transitScore)} type="transit" />
          ) : null}

          {bikeScore && bikeScore > 0 ? (
            <ScoreCard value={Math.round(bikeScore)} type="bike" />
          ) : null}
        </div>

        <div className="text-xs text-muted-foreground flex items-start gap-1.5">
          <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
          <p>
            Scores are out of 100. Higher scores indicate better accessibility.
            Data provided by Walk Score®.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
